import {
	ClipboardCheck,
	ClipboardList,
	FileWarning,
	Gauge,
	MessageSquareMore,
	Settings,
	Target,
	UserRoundX,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { CardButton } from '@/components/card-button'
import { PersonalDashboard } from '@/components/personal-dashboard'
import { PushNotificationsCard } from '@/components/push-notifications-card'
import { hasRouteAccess } from '@/constants/permissions'
import { ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/store'

type ModuleSection = 'register' | 'follow' | 'manage'

interface Module {
	id: string
	section: ModuleSection
	icon: React.ReactNode
	route: string
	iconBg: string
	borderColor: string
}

// Módulos separados pelo que a pessoa vai fazer: registrar, acompanhar ou administrar
const SECTIONS: ModuleSection[] = ['register', 'follow', 'manage']

export function HomePage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const user = useAuthStore((s) => s.user)

	const allModules: Module[] = [
		{
			id: 'inspections',
			section: 'register',
			icon: <ClipboardCheck className="size-5 text-kaefer-sapphire-blue" />,
			route: ROUTES.INSPECTIONS.INDEX,
			iconBg: 'bg-kaefer-sapphire-blue/10',
			borderColor: 'border-l-kaefer-sapphire-blue',
		},
		{
			id: 'dds',
			section: 'register',
			icon: <MessageSquareMore className="size-5 text-kaefer-honey-yellow" />,
			route: ROUTES.DDS.HUB,
			iconBg: 'bg-kaefer-honey-yellow/10',
			borderColor: 'border-l-kaefer-honey-yellow',
		},
		{
			id: 'campaigns',
			section: 'register',
			icon: <ClipboardList className="size-5 text-kaefer-violet" />,
			route: ROUTES.DASHBOARD.CAMPAIGNS,
			iconBg: 'bg-kaefer-violet/10',
			borderColor: 'border-l-kaefer-violet',
		},
		{
			id: 'occurrences',
			section: 'register',
			icon: <FileWarning className="size-5 text-kaefer-pumpkin-orange" />,
			route: ROUTES.OCCURRENCES.INDEX,
			iconBg: 'bg-kaefer-pumpkin-orange/10',
			borderColor: 'border-l-kaefer-pumpkin-orange',
		},
		{
			id: 'administration',
			section: 'manage',
			icon: <Settings className="size-5 text-kaefer-medium-grey" />,
			route: ROUTES.ADMINISTRATION.INDEX,
			iconBg: 'bg-kaefer-medium-grey/10',
			borderColor: 'border-l-kaefer-medium-grey',
		},
		{
			id: 'myCommitmentExceptions',
			section: 'follow',
			icon: <UserRoundX className="size-5 text-kaefer-red" />,
			route: ROUTES.DASHBOARD.MY_COMMITMENT_EXCEPTIONS,
			iconBg: 'bg-kaefer-red/10',
			borderColor: 'border-l-kaefer-red',
		},
		{
			id: 'commitmentMatrixDashboard',
			section: 'follow',
			icon: <Target className="size-5 text-kaefer-forest-green" />,
			route: ROUTES.DASHBOARD.COMMITMENT_MATRIX_DASHBOARD,
			iconBg: 'bg-kaefer-forest-green/10',
			borderColor: 'border-l-kaefer-forest-green',
		},
		{
			id: 'kpiMetrics',
			section: 'follow',
			icon: <Gauge className="size-5 text-kaefer-medium-grey" />,
			route: ROUTES.ADMINISTRATION.KPI_METRICS,
			iconBg: 'bg-kaefer-medium-grey/10',
			borderColor: 'border-l-kaefer-medium-grey',
		},
	]

	const modules = user
		? allModules.filter((m) => hasRouteAccess(user.role, m.route, user.badge))
		: allModules

	return (
		<div className="px-4 py-6 sm:px-6">
			<PushNotificationsCard variant="banner" />
			<PersonalDashboard />

			{SECTIONS.map((section) => {
				const sectionModules = modules.filter((m) => m.section === section)
				if (sectionModules.length === 0) return null
				return (
					<section key={section} className="mb-6">
						<h2 className="mb-3 text-sm font-semibold text-kaefer-dark-grey">
							{t(`home.sections.${section}`)}
						</h2>
						<div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-3">
							{sectionModules.map((module) => (
								<CardButton
									key={module.id}
									icon={module.icon}
									iconBg={module.iconBg}
									borderColor={module.borderColor}
									title={t(`home.modules.${module.id}.title`)}
									description={t(`home.modules.${module.id}.description`)}
									onClick={() => navigate(module.route)}
								/>
							))}
						</div>
					</section>
				)
			})}
		</div>
	)
}
